/**
 * Instructor directory — /pages/instructors
 *
 * Markup: sections/instructor-profiles.liquid + snippets/instructor-filters.liquid
 * Styles: assets/instructor-profiles.css
 *
 * How it works
 * 1. Every active `instructor_profiles` metaobject is loaded from the Storefront
 *    API before any card is shown. Draft entries are never returned by Shopify.
 *    The list is read from both ends at once (normal + reverse order) and the two
 *    halves are joined by handle, which roughly halves the wait.
 * 2. Order is decided once: profiles with a `priority` (1, 2, 3 …) first, the
 *    rest shuffled. A new visit or reload gets a new shuffle; back/forward keeps
 *    the previous one so the visitor lands on the same card.
 * 3. Search matches the full name only. Country, Specialties, Level and Language
 *    are filters: OR inside a group, AND across groups.
 * 4. Filter options are the official lists below. A profile value that is not
 *    on the list (after trimming and turning – — into -) is not offered as an
 *    option — fix the profile data instead.
 *
 * Required Storefront API scopes for the token in Theme settings:
 * unauthenticated_read_metaobjects, unauthenticated_read_product_listings (images).
 */
(() => {
  'use strict';

  // Storefront API versions are supported for 12 months. 2026-10 is supported
  // until 16 Oct 2027 — move to the newest stable version before then.
  const API_VERSION = '2026-10';
  const METAOBJECT_TYPE = 'instructor_profiles';
  const PAGE_SIZE = 250;
  const CACHE_KEY = `ipd:data:${API_VERSION}`;
  const CACHE_TTL_MS = 10 * 60 * 1000;
  const SEARCH_MIN_CHARS = 2;
  const REQUEST_TIMEOUT_MS = 20000;
  const MAX_RETRIES = 2;
  const URL_WRITE_DELAY_MS = 250;
  // Cards are at most ~360 CSS px wide; 720px covers 2× screens without
  // sending 3× files to phones.
  const IMAGE_WIDTHS = [240, 360, 480, 720];
  const IMAGE_SIZES =
    '(min-width: 1520px) 330px, (min-width: 1200px) calc((100vw - 128px) / 4 - 16px), ' +
    '(min-width: 1024px) calc((100vw - 112px) / 3 - 16px), (min-width: 750px) calc((100vw - 96px) / 2 - 16px), ' +
    '(min-width: 640px) calc((100vw - 48px) / 2 - 16px), calc(100vw - 48px)';

  // Letters that do not decompose under NFD, for accent-insensitive matching.
  const FOLD_EXTRA = { ł: 'l', ø: 'o', đ: 'd', ð: 'd', ı: 'i', æ: 'ae', œ: 'oe', ß: 'ss', þ: 'th' };

  const GROUPS = [
    { key: 'country', param: 'country', field: 'countries', label: 'Country', options: [
      "American Samoa",
      "Argentina",
      "Australia",
      "Austria",
      "Bahrain",
      "Belgium",
      "Brazil",
      "Bulgaria",
      "Canada",
      "Canary Islands",
      "Chile",
      "China",
      "Colombia",
      "Costa Rica",
      "Croatia",
      "Cyprus",
      "Czech Republic",
      "Denmark",
      "Dominican Republic",
      "Ecuador",
      "Egypt",
      "Estonia",
      "Finland",
      "France",
      "Georgia",
      "Germany",
      "Ghana",
      "Greece",
      "Guatemala",
      "Hawaii",
      "Honduras",
      "Hong Kong",
      "Hungary",
      "Iceland",
      "India",
      "Indonesia",
      "Ireland",
      "Israel",
      "Italy",
      "Japan",
      "Jordan",
      "Kuwait",
      "Latvia",
      "Lebanon",
      "Liechtenstein",
      "Lithuania",
      "Luxembourg",
      "Malaysia",
      "Malta",
      "Martinique",
      "Mexico",
      "Moldova",
      "Monaco",
      "Morocco",
      "Namibia",
      "Netherlands",
      "New Zealand",
      "Nicaragua",
      "North Macedonia",
      "Northern Ireland",
      "Norway",
      "Panama",
      "Paraguay",
      "Peru",
      "Philippines",
      "Poland",
      "Portugal",
      "Puerto Rico",
      "Qatar",
      "Romania",
      "Russia",
      "Réunion Island",
      "Saudi Arabia",
      "Scotland",
      "Serbia",
      "Singapore",
      "Slovakia",
      "Slovenia",
      "South Africa",
      "South Korea",
      "Spain",
      "Sri Lanka",
      "Swaziland",
      "Sweden",
      "Switzerland",
      "Taiwan",
      "Thailand",
      "Turkey",
      "Ukraine",
      "United Arab Emirates",
      "United Kingdom",
      "United States of America",
      "Uruguay",
      "Venezuela",
      "Vietnam",
      "Zambia"
    ] },
    { key: 'specialties', param: 'specialty', field: 'specialties', label: 'Specialty', options: [
      "Acupuncture",
      "Addiction/Trauma",
      "Biohacking",
      "Buteyko Method",
      "ChiRunning",
      "Chiropractic",
      "Chronic Disease",
      "Corporate Wellness",
      "Corrective Movement Pattern Exercise",
      "Crossfit",
      "Cycling",
      "Dance",
      "Equestrian sport",
      "Extreme Sports",
      "Fitness",
      "Freediving",
      "Golf",
      "Health & Wellness Coaching",
      "Healthcare",
      "Holistic Wellbeing",
      "Hormonal Health",
      "Hypopressive",
      "Individual Sports",
      "Kinstretch",
      "Lifestyle Coaching",
      "Martial Arts",
      "Meditation",
      "Mental Health Coaching",
      "Movement Coaching",
      "Nervous System regulation",
      "Nordic Walking",
      "Nutritional Therapy & Weight Loss",
      "Orofacial Myofunctional Therapy",
      "Osteopathy",
      "Padding sports",
      "Performance",
      "Physical Therapy",
      "Pilates",
      "Psychology",
      "Public speaking",
      "Qigong",
      "Running",
      "Sleep Therapy",
      "Sports Rehabilitation",
      "Strength & Conditioning",
      "Swimming",
      "Team Sports",
      "Trailrunning",
      "Voice Coaching",
      "Wim Hof Method",
      "Yoga"
    ] },
    { key: 'levels', param: 'level', field: 'levels', label: 'Level', options: [
      "Level 1 - Functional",
      "Level 1 - Yoga",
      "Level 2 - Advanced",
      "Master Instructor"
    ] },
    { key: 'languages', param: 'language', field: 'languages', label: 'Language', options: [
      "Afrikaans",
      "Arabic",
      "Armenian",
      "Basque",
      "Bisaya",
      "Brazilian",
      "Bulgarian",
      "Catalan",
      "Chinese",
      "Croatian",
      "Czech",
      "Danish",
      "Dutch",
      "English",
      "Finnish",
      "French",
      "German",
      "Greek",
      "Gujarati",
      "Hebrew",
      "Hindi",
      "Hungarian",
      "Italian",
      "Japanese",
      "Korean",
      "Latvian",
      "Lithuanian",
      "Luxembourgish",
      "Macedonian",
      "Norwegian",
      "Polish",
      "Portuguese",
      "Romanian",
      "Russian",
      "Serbian",
      "Slovakian",
      "Slovenian",
      "Spanish",
      "Swedish",
      "Tagalog",
      "Tamil",
      "Telugu",
      "Thai",
      "Turkish",
      "Ukrainian",
      "Welsh"
    ] }
  ];
  const GROUP_BY_KEY = new Map(GROUPS.map((group) => [group.key, group]));
  // Folded once — used by the search suggestions on every keystroke.
  for (const group of GROUPS) group.folded = group.options.map(fold);
  const LEVEL_ORDER = GROUP_BY_KEY.get('levels').options;

  const QUERY = `
    query InstructorDirectory($after: String, $reverse: Boolean) {
      metaobjects(type: "${METAOBJECT_TYPE}", first: ${PAGE_SIZE}, after: $after, reverse: $reverse, sortKey: "id") {
        pageInfo { hasNextPage endCursor }
        nodes {
          handle
          onlineStoreUrl
          name: field(key: "full_name") { value }
          country: field(key: "country") { value }
          region: field(key: "region") { value }
          city: field(key: "city") { value }
          specialties: field(key: "specialties") { value }
          levels: field(key: "instructor_levels") { value }
          languages: field(key: "languages") { value }
          priority: field(key: "priority") { value }
          image: field(key: "profile_image") {
            reference { ... on MediaImage { image { url width height altText thumbhash } } }
          }
        }
      }
    }`;

  // ---------------------------------------------------------------------------
  // Start — on page load, and again when the section is re-rendered in the
  // theme editor.
  // ---------------------------------------------------------------------------
  const instances = new Map(); // root -> teardown

  function start(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, initDirectory(root));
  }

  start(document.querySelector('[data-ipd]'));

  document.addEventListener('shopify:section:load', (event) => start(event.target.querySelector('[data-ipd]')));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, teardown] of instances) {
      if (event.target.contains(root)) {
        teardown();
        instances.delete(root);
      }
    }
  });

  function initDirectory(root) {
    const $ = (selector, scope = root) => scope.querySelector(selector);
    const $$ = (selector, scope = root) => Array.from(scope.querySelectorAll(selector));
    // Removes every document/window listener of this instance on teardown.
    const lifetime = new AbortController();
    const signal = lifetime.signal;

    const config = readConfig();
    const el = {
      searchInput: $('[data-ipd-search]'),
      searchClear: $('[data-ipd-search-clear]'),
      suggestions: $('[data-ipd-suggestions]'),
      toolbar: $('.ipd-toolbar'),
      dialog: $('[data-ipd-dialog]'),
      dialogCount: $('[data-ipd-dialog-count]'),
      sheetOpen: $('[data-ipd-dialog-open]'),
      sheetGroups: $$('[data-ipd-sheet-group]'),
      active: $('[data-ipd-active]'),
      pills: $('[data-ipd-pills]'),
      counter: $('[data-ipd-counter]'),
      announcer: $('[data-ipd-announcer]'),
      grid: $('[data-ipd-grid]'),
      sentinel: $('[data-ipd-sentinel]'),
      empty: $('[data-ipd-empty]'),
      emptyTitle: $('[data-ipd-empty-title]'),
      emptyText: $('[data-ipd-empty-text]'),
      emptyAction: $('[data-ipd-empty-action]'),
      error: $('[data-ipd-error]'),
      toTop: $('[data-ipd-to-top]'),
      cardTemplate: $('[data-ipd-card-template]')
    };
    // Kept so a "Try again" after an error shows the same loading skeleton.
    const skeletons = Array.from(el.grid.children, (node) => node.cloneNode(true));

    const state = {
      status: 'loading', // loading | ready | error
      records: [], // final display order
      results: [], // records matching the current search + filters
      rendered: 0, // how many of `results` are in the grid
      counts: emptyCounts(),
      query: '',
      filters: Object.fromEntries(GROUPS.map((group) => [group.key, new Set()])),
      gridStale: false, // a change happened while the mobile sheet was open
      restorePending: false,
      sheetRendered: false,
      suggestionIndex: -1
    };

    const cards = new Map(); // handle -> card element for the current results
    const numberFormat = new Intl.NumberFormat('en');
    const navigationType = performance.getEntriesByType?.('navigation')[0]?.type || 'navigate';
    const storage = createStorage();
    let openDropdown = null;
    let searchTimer;
    let urlTimer;
    let announceTimer;
    let toTopFrame = 0;
    let loadRun = 0;

    const scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) appendBatch();
      },
      { rootMargin: '0px 0px 120% 0px' }
    );

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    trackStickyHeader();
    readStateFromURL();
    renderAllOptions();
    syncControls();
    bindEvents();
    load();

    return function teardown() {
      lifetime.abort();
      scrollObserver.disconnect();
      clearTimeout(searchTimer);
      clearTimeout(urlTimer);
      clearTimeout(announceTimer);
      loadRun++;
    };

    // -------------------------------------------------------------------------
    // Config
    // -------------------------------------------------------------------------
    function readConfig() {
      try {
        const parsed = JSON.parse($('[data-ipd-config]').textContent);
        const batch = Number.parseInt(parsed.batchSize, 10);
        return {
          shopDomain: String(parsed.shopDomain || ''),
          primaryDomain: String(parsed.primaryDomain || ''),
          token: String(parsed.storefrontToken || ''),
          batchSize: batch >= 6 && batch <= 24 ? batch : 12,
          randomize: parsed.randomizeOrder !== false
        };
      } catch {
        return { shopDomain: '', primaryDomain: '', token: '', batchSize: 12, randomize: true };
      }
    }

    // On the shop's own domain the API is called same-origin: no CORS preflight
    // and no extra connection. Anywhere else (theme preview links) it falls back
    // to the .myshopify.com domain.
    function apiEndpoint() {
      const host = window.location.hostname;
      if (host && (host === config.primaryDomain || host === config.shopDomain)) {
        return `/api/${API_VERSION}/graphql.json`;
      }
      return `https://${config.shopDomain}/api/${API_VERSION}/graphql.json`;
    }

    // -------------------------------------------------------------------------
    // Loading
    // -------------------------------------------------------------------------
    async function load() {
      const run = ++loadRun;
      setStatus('loading');
      try {
        let records = readCache();
        if (!records) {
          records = await fetchAllRecords();
          writeCache(records);
        }
        if (run !== loadRun) return; // torn down or restarted meanwhile
        state.records = orderRecords(records);
        state.counts = countValues(state.records);
        setStatus('ready');
        renderAllOptions();
        update({ keepScroll: true });
        restoreReturnPosition();
      } catch (error) {
        if (run !== loadRun) return;
        console.error('[Instructor directory] Could not load profiles:', error);
        setStatus('error');
      }
    }

    async function fetchAllRecords() {
      if (!config.shopDomain || !config.token) {
        throw new Error('Missing shop domain or Storefront API token (Theme settings → storefront_api_token).');
      }

      const byHandle = new Map();
      const stop = new AbortController(); // cancels the other walker if one fails
      let finished = false;
      let failed = false;

      // Walks the list from one end. Stops when it reaches a profile the other
      // walker already has, or the end of the list.
      const walk = async (reverse) => {
        let after = null;
        try {
          while (!finished) {
            const page = await request({ after, reverse }, stop.signal, () => finished);
            if (!page) return; // the list was completed by the other walker
            for (const node of page.nodes) {
              const seenFromOtherEnd = byHandle.has(node.handle) && byHandle.get(node.handle).reverse !== reverse;
              if (seenFromOtherEnd) finished = true;
              else if (!byHandle.has(node.handle)) byHandle.set(node.handle, { node, reverse });
            }
            if (!page.pageInfo.hasNextPage) finished = true;
            after = page.pageInfo.endCursor;
          }
        } catch (error) {
          // A failure on a page that is no longer needed is not a failure.
          if (finished && !failed) return;
          failed = true;
          finished = true;
          stop.abort();
          throw error;
        }
      };

      await Promise.all([walk(false), walk(true)]);
      return Array.from(byHandle.values(), ({ node }) => toRecord(node)).filter(Boolean);
    }

    // Returns null when `isDone()` turns true while waiting to retry.
    async function request(variables, stopSignal, isDone, attempt = 0) {
      if (stopSignal.aborted) throw new Error('Loading cancelled');
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      const cancel = () => controller.abort();
      stopSignal.addEventListener('abort', cancel, { once: true });
      try {
        const response = await fetch(apiEndpoint(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': config.token
          },
          body: JSON.stringify({ query: QUERY, variables }),
          signal: controller.signal
        });
        if (!response.ok) {
          const error = new Error(`HTTP ${response.status}`);
          // A wrong token or a bad request will not fix itself — fail fast.
          error.permanent = response.status >= 400 && response.status < 500 && response.status !== 429;
          throw error;
        }
        const json = await response.json();
        if (json.errors?.length) throw new Error(json.errors.map((e) => e.message).join('; '));
        if (!json.data?.metaobjects) throw new Error('Unexpected API response');
        return json.data.metaobjects;
      } catch (error) {
        if (error.permanent || stopSignal.aborted || attempt >= MAX_RETRIES) throw error;
        await new Promise((resolve) => setTimeout(resolve, 600 * 2 ** attempt));
        if (isDone()) return null;
        return request(variables, stopSignal, isDone, attempt + 1);
      } finally {
        clearTimeout(timer);
        stopSignal.removeEventListener('abort', cancel);
      }
    }

    // -------------------------------------------------------------------------
    // Session cache (best effort — storage may be unavailable or full)
    // -------------------------------------------------------------------------
    function readCache() {
      const cached = storage.get(CACHE_KEY);
      if (!cached) return null;
      try {
        const { savedAt, records } = JSON.parse(cached);
        if (Date.now() - savedAt < CACHE_TTL_MS && Array.isArray(records) && records.length) {
          // Re-check links: the cache is outside this script's control.
          return records
            .filter((record) => record && typeof record.handle === 'string')
            .map((record) => ({ ...record, url: safeProfilePath(record.url, record.handle) }));
        }
      } catch {
        // corrupt entry — refetch
      }
      storage.remove(CACHE_KEY);
      return null;
    }

    function writeCache(records) {
      storage.set(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), records }));
    }

    // -------------------------------------------------------------------------
    // Order: priority first, then a shuffle that is stable for back/forward
    // -------------------------------------------------------------------------
    function orderRecords(records) {
      const byName = (a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
      const prioritized = records
        .filter((r) => r.priority !== null)
        .sort((a, b) => a.priority - b.priority || byName(a, b));
      const rest = records.filter((r) => r.priority === null);

      if (!config.randomize) return [...prioritized, ...rest.sort(byName)];

      // Shuffle in handle order so the same seed always gives the same result.
      rest.sort((a, b) => (a.handle < b.handle ? -1 : 1));
      const random = mulberry32(shuffleSeed());
      for (let i = rest.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [rest[i], rest[j]] = [rest[j], rest[i]];
      }
      return [...prioritized, ...rest];
    }

    // The seed lives on this history entry, so Back/Forward to any earlier visit
    // of the directory brings back exactly the order that visit had.
    function shuffleSeed() {
      const stored = Number(history.state?.ipdSeed);
      if (navigationType === 'back_forward' && Number.isInteger(stored)) return stored;
      const seed = crypto.getRandomValues(new Uint32Array(1))[0];
      patchHistoryState({ ipdSeed: seed, ipdReturn: null });
      return seed;
    }

    // -------------------------------------------------------------------------
    // Filtering and counts
    // -------------------------------------------------------------------------
    // Per-option count = how many instructors have that value (whole directory).
    function countValues(records) {
      const counts = emptyCounts();
      for (const record of records) {
        for (const group of GROUPS) {
          const map = counts[group.key];
          for (const value of record[group.field]) map.set(value, (map.get(value) || 0) + 1);
        }
      }
      return counts;
    }

    function matches(record, tokens) {
      if (tokens.length) {
        if (!record.searchKey) record.searchKey = fold(record.name);
        if (!tokens.every((token) => record.searchKey.includes(token))) return false;
      }
      for (const group of GROUPS) {
        const selected = state.filters[group.key];
        if (selected.size && !record[group.field].some((value) => selected.has(value))) return false;
      }
      return true;
    }

    function queryTokens() {
      return state.query.length >= SEARCH_MIN_CHARS ? fold(state.query).split(/\s+/).filter(Boolean) : [];
    }

    function hasActiveFilters() {
      return GROUPS.some((group) => state.filters[group.key].size > 0);
    }

    // -------------------------------------------------------------------------
    // Update cycle: state -> results -> UI -> URL
    // -------------------------------------------------------------------------
    function update({ keepScroll = false } = {}) {
      if (state.status === 'ready') {
        const tokens = queryTokens();
        state.results = state.records.filter((record) => matches(record, tokens));
        if (el.dialog?.open) {
          state.gridStale = true;
        } else {
          renderGrid();
          if (!keepScroll) scrollToResults();
        }
        renderCounter();
        announce(counterText());
      }
      syncControls();
      scheduleURLWrite();
    }

    function renderGrid() {
      state.gridStale = false;
      // Forget cards that are no longer in the results so their elements (and
      // any listeners third-party scripts attach to links) can be freed.
      const keep = new Set(state.results.map((record) => record.handle));
      for (const handle of cards.keys()) if (!keep.has(handle)) cards.delete(handle);

      state.rendered = Math.min(config.batchSize, state.results.length);
      el.grid.replaceChildren(...state.results.slice(0, state.rendered).map((record, i) => cardFor(record, i)));
      el.grid.setAttribute('aria-busy', 'false');
      renderEmptyState();
      rearmInfiniteScroll();
    }

    function appendBatch() {
      if (state.status !== 'ready' || state.gridStale || state.rendered >= state.results.length) return;
      const next = state.results.slice(state.rendered, state.rendered + config.batchSize);
      el.grid.append(...next.map((record, i) => cardFor(record, state.rendered + i)));
      state.rendered += next.length;
      rearmInfiniteScroll();
    }

    function renderUpTo(count) {
      while (state.rendered < Math.min(count, state.results.length)) {
        const before = state.rendered;
        appendBatch();
        if (state.rendered === before) break;
      }
    }

    function renderEmptyState() {
      const isEmpty = state.results.length === 0;
      el.empty.hidden = !isEmpty;
      el.counter.hidden = isEmpty; // the empty state says it already
      if (!isEmpty) return;
      if (hasActiveFilters()) {
        el.emptyTitle.textContent = 'No instructors match your filters';
        el.emptyText.textContent = 'Try removing some filters.';
        el.emptyAction.textContent = 'Clear all filters';
      } else {
        el.emptyTitle.textContent = `No instructors named “${state.query}”`;
        el.emptyText.textContent = 'Check the spelling or try another name.';
        el.emptyAction.textContent = 'Clear search';
      }
    }

    function counterText() {
      const total = state.results.length;
      if (total === 0) return 'No instructors found';
      const label = `${numberFormat.format(total)} instructor${total === 1 ? '' : 's'}`;
      return state.query || hasActiveFilters() ? `${label} found` : label;
    }

    function renderCounter() {
      el.counter.textContent = counterText();
      el.counter.classList.remove('is-loading');
      if (el.dialogCount) {
        const total = state.results.length;
        el.dialogCount.textContent = total
          ? `Show ${numberFormat.format(total)} instructor${total === 1 ? '' : 's'}`
          : 'No instructors found';
        el.dialogCount.classList.toggle('is-muted', total === 0);
      }
    }

    function announce(text) {
      clearTimeout(announceTimer);
      announceTimer = setTimeout(() => {
        el.announcer.textContent = text;
      }, 500);
    }

    function setStatus(status) {
      state.status = status;
      root.dataset.status = status;
      el.grid.setAttribute('aria-busy', String(status === 'loading'));
      el.error.hidden = status !== 'error';
      if (status === 'loading') {
        if (!el.grid.children.length) el.grid.replaceChildren(...skeletons.map((node) => node.cloneNode(true)));
        el.counter.hidden = false;
        el.counter.textContent = 'Loading instructors…';
        el.counter.classList.add('is-loading');
        el.empty.hidden = true;
      }
      if (status === 'error') {
        el.grid.replaceChildren();
        el.counter.hidden = true;
        el.counter.classList.remove('is-loading');
        el.empty.hidden = true;
      }
    }

    // -------------------------------------------------------------------------
    // Scrolling helpers
    // -------------------------------------------------------------------------
    // Height of the theme's sticky header as actually drawn. The theme's
    // --header-height can be a few pixels off, which would tuck the sticky
    // search bar under the header on phones.
    function trackStickyHeader() {
      const group = document.getElementById('header-group') || document.querySelector('header');
      if (!group) return;
      const candidates = [group, ...group.querySelectorAll(':scope > *, :scope > * > *')];
      const sticky = candidates.find((node) => ['sticky', 'fixed'].includes(getComputedStyle(node).position));
      if (!sticky) return;
      const apply = () => root.style.setProperty('--ipd-sticky-top', `${Math.round(sticky.getBoundingClientRect().height)}px`);
      apply();
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(apply);
        observer.observe(sticky);
        signal.addEventListener('abort', () => observer.disconnect());
      }
    }

    function stickyOffset() {
      const header = Number.parseFloat(getComputedStyle(root).getPropertyValue('--ipd-sticky-top')) ||
        Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const toolbar = getComputedStyle(el.toolbar).position === 'sticky' ? el.toolbar.offsetHeight : 0;
      return header + toolbar;
    }

    // After a change made further down the page, bring the selected filters and
    // the counter back into view, below the sticky header (and, on phones, the
    // sticky search bar).
    function scrollToResults() {
      const anchor = !el.active.hidden ? el.active : !el.counter.hidden ? el.counter : el.grid;
      const top = anchor.getBoundingClientRect().top + window.scrollY - stickyOffset() - 12;
      if (window.scrollY > top + 1) jump(() => window.scrollTo(0, Math.max(0, top)));
    }

    // The theme sets `scroll-behavior: smooth` on <html>; repositioning after a
    // filter change or a return from a profile must be instant, not animated.
    function jump(scroll) {
      const html = document.documentElement;
      const previous = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      scroll();
      html.style.scrollBehavior = previous;
    }

    // IntersectionObserver only reports changes. Re-observing after every render
    // forces a fresh report, so loading continues while the sentinel stays near
    // the viewport (large screens, short result lists).
    function rearmInfiniteScroll() {
      scrollObserver.unobserve(el.sentinel);
      if (state.rendered < state.results.length) scrollObserver.observe(el.sentinel);
    }

    // -------------------------------------------------------------------------
    // Cards
    // -------------------------------------------------------------------------
    function cardFor(record, index) {
      let card = cards.get(record.handle);
      if (!card) {
        card = buildCard(record, index);
        cards.set(record.handle, card);
      }
      return card;
    }

    function buildCard(record, index) {
      const card = el.cardTemplate.content.firstElementChild.cloneNode(true);
      card.href = record.url;
      card.dataset.handle = record.handle;

      const media = $('[data-card-media]', card);
      if (record.image) {
        const placeholder = thumbhashDataURL(record.image.thumbhash);
        if (placeholder) media.style.backgroundImage = `url("${placeholder}")`;
        media.append(buildImage(record, index < 4));
      } else {
        media.append(initialsFor(record.name));
      }

      if (record.priority !== null) $('[data-card-featured]', card).hidden = false;

      const levels = $('[data-card-levels]', card);
      if (record.levels.length) {
        levels.append(
          ...sortLevels(record.levels).map((level) => {
            const chip = document.createElement('li');
            chip.className = 'ipd-chip';
            chip.textContent = level;
            return chip;
          })
        );
      } else {
        levels.remove();
      }

      $('[data-card-name]', card).textContent = record.name;

      const location = locationText(record);
      if (location) $('[data-card-location-text]', card).textContent = location;
      else $('[data-card-location]', card).remove();

      return card;
    }

    function buildImage(record, eager) {
      const { url, width, alt } = record.image;
      // Never ask the CDN for more pixels than the original has: it would return
      // the same file under several URLs.
      let widths = width ? IMAGE_WIDTHS.filter((w) => w <= width) : IMAGE_WIDTHS.slice(0, 3);
      if (!widths.length) widths = [width];

      const img = document.createElement('img');
      img.className = 'ipd-card__img';
      img.alt = alt || record.name;
      img.width = 480;
      img.height = 480;
      img.decoding = 'async';
      img.loading = eager ? 'eager' : 'lazy';
      if (eager) img.setAttribute('fetchpriority', 'high');
      img.sizes = IMAGE_SIZES;
      img.srcset = widths.map((w) => `${cdnUrl(url, w)} ${w}w`).join(', ');
      img.src = cdnUrl(url, widths[Math.min(2, widths.length - 1)]);

      const markLoaded = () => {
        img.classList.add('is-loaded');
        // The blurred preview is no longer visible — drop it from the style.
        setTimeout(() => img.parentElement?.style.removeProperty('background-image'), 400);
      };
      img.addEventListener('load', markLoaded, { once: true });
      img.addEventListener(
        'error',
        () => {
          const media = img.parentElement;
          img.remove();
          media?.append(initialsFor(record.name));
        },
        { once: true }
      );
      if (img.complete && img.naturalWidth) markLoaded();
      return img;
    }

    // -------------------------------------------------------------------------
    // Filter options (desktop dropdowns and the mobile sheet share one renderer;
    // the sheet's copy is only built the first time it is opened)
    // -------------------------------------------------------------------------
    function renderAllOptions() {
      for (const container of $$('[data-ipd-options]')) {
        const inSheet = Boolean(container.closest('[data-ipd-dialog]'));
        if (inSheet && !state.sheetRendered) continue;
        renderOptions(container, !inSheet);
      }
      // Keep any text typed into an option search while the data arrived.
      for (const search of $$('[data-ipd-option-search]')) if (search.value) filterOptionList(search);
      for (const panel of $$('[data-ipd-dropdown-panel]')) resetRoving(panel);
    }

    function renderOptions(container, roving) {
      const group = GROUP_BY_KEY.get(container.dataset.ipdOptions);
      if (!group) return;
      const fragment = document.createDocumentFragment();
      group.options.forEach((value, i) => {
        const label = document.createElement('label');
        label.className = 'ipd-option';
        label.dataset.value = group.folded[i];

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'ipd-option__input';
        input.value = value;
        input.dataset.group = group.key;
        // Desktop panels: one tab stop for the whole list, arrows move inside.
        if (roving) input.tabIndex = -1;

        const text = document.createElement('span');
        text.className = 'ipd-option__label';
        text.textContent = value;

        const count = document.createElement('span');
        count.className = 'ipd-option__count';
        if (state.status === 'ready') {
          const n = state.counts[group.key].get(value) || 0;
          count.textContent = numberFormat.format(n);
          if (n === 0) {
            label.classList.add('is-empty');
            input.dataset.empty = 'true';
          }
        } else {
          count.classList.add('is-loading');
          count.setAttribute('aria-hidden', 'true');
        }

        label.append(input, text, count);
        fragment.append(label);
      });
      container.replaceChildren(fragment);
    }

    function syncControls() {
      for (const input of $$('input[data-group]')) {
        const checked = state.filters[input.dataset.group]?.has(input.value) || false;
        input.checked = checked;
        // An option nobody has is disabled, unless it is selected (so it can be cleared).
        input.disabled = input.dataset.empty === 'true' && !checked;
      }

      for (const group of GROUPS) {
        const size = state.filters[group.key].size;
        for (const badge of $$(`[data-ipd-badge="${group.key}"]`)) {
          badge.textContent = String(size);
          badge.hidden = size === 0;
          // Screen readers hear "Country, 2 selected" rather than "Country 2".
          const control = badge.closest('button, summary');
          const name = control?.querySelector('.ipd-filter__label')?.textContent.trim();
          if (control && name) {
            if (size) control.setAttribute('aria-label', `${name}, ${size} selected`);
            else control.removeAttribute('aria-label');
          }
        }
        for (const label of $$(`[data-ipd-selected="${group.key}"]`)) {
          label.textContent = size ? `${size} selected` : 'None selected';
        }
        for (const clear of $$(`[data-ipd-group-clear="${group.key}"]`)) clear.hidden = size === 0;
      }

      const totalSelected = GROUPS.reduce((sum, group) => sum + state.filters[group.key].size, 0);
      const openBadge = $('[data-ipd-badge="all"]');
      if (openBadge) {
        openBadge.textContent = String(totalSelected);
        openBadge.hidden = totalSelected === 0;
        if (totalSelected) el.sheetOpen.setAttribute('aria-label', `Filters, ${totalSelected} selected`);
        else el.sheetOpen.removeAttribute('aria-label');
      }

      if (el.searchInput.value !== state.query && document.activeElement !== el.searchInput) {
        el.searchInput.value = state.query;
      }
      el.searchClear.hidden = el.searchInput.value.length === 0;

      renderPills();
    }

    function renderPills() {
      const pills = [];
      for (const group of GROUPS) {
        for (const value of state.filters[group.key]) {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'ipd-pill';
          pill.dataset.group = group.key;
          pill.dataset.value = value;
          pill.setAttribute('aria-label', `Remove filter: ${value}`);
          const text = document.createElement('span');
          text.textContent = value;
          pill.append(text, closeIcon());
          pills.push(pill);
        }
      }
      el.pills.replaceChildren(...pills);
      el.active.hidden = pills.length === 0 && state.query.length === 0;
    }

    function setFilter(groupKey, value, on) {
      const selected = state.filters[groupKey];
      if (!selected) return;
      if (on) selected.add(value);
      else selected.delete(value);
    }

    function clearFilters({ includeSearch }) {
      for (const group of GROUPS) state.filters[group.key].clear();
      if (includeSearch) {
        state.query = '';
        el.searchInput.value = '';
      }
    }

    // -------------------------------------------------------------------------
    // URL + history state — the address bar always describes what is on screen
    // -------------------------------------------------------------------------
    function readStateFromURL() {
      const params = new URLSearchParams(window.location.search);
      const search = (params.get('search') || '').trim().slice(0, 100);
      state.query = search.length >= SEARCH_MIN_CHARS ? search : '';
      el.searchInput.value = state.query;

      for (const group of GROUPS) {
        const raw = params.getAll(group.param).flatMap((value) => value.split(','));
        for (const value of raw) {
          const clean = cleanValue(value);
          if (group.options.includes(clean)) state.filters[group.key].add(clean);
        }
      }
    }

    // Batched: Safari refuses more than ~100 history updates per 10 seconds.
    function scheduleURLWrite() {
      clearTimeout(urlTimer);
      urlTimer = setTimeout(writeStateToURL, URL_WRITE_DELAY_MS);
    }

    function writeStateToURL() {
      clearTimeout(urlTimer);
      const params = new URLSearchParams(window.location.search);
      params.delete('search');
      for (const group of GROUPS) params.delete(group.param);

      if (state.query) params.set('search', state.query);
      for (const group of GROUPS) {
        const values = Array.from(state.filters[group.key]);
        if (values.length) params.set(group.param, values.join(','));
      }

      const query = params.toString();
      const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
        try {
          history.replaceState(history.state, '', next);
        } catch {
          // rate-limited or sandboxed: the page keeps working, only the URL lags
        }
      }
    }

    function patchHistoryState(patch) {
      try {
        history.replaceState({ ...(history.state || {}), ...patch }, '');
      } catch {
        // see writeStateToURL
      }
    }

    // -------------------------------------------------------------------------
    // Returning from a profile: land on the same card. Stored on this history
    // entry, so it works for every Back to it, not just the first.
    // -------------------------------------------------------------------------
    function rememberReturnPoint(handle) {
      writeStateToURL();
      patchHistoryState({ ipdReturn: { handle, rendered: state.rendered } });
    }

    function restoreReturnPosition() {
      const saved = history.state?.ipdReturn;
      if (navigationType !== 'back_forward' || !saved?.handle) return;
      if (state.gridStale) {
        state.restorePending = true; // the mobile sheet is open; do it on close
        return;
      }
      state.restorePending = false;
      const index = state.results.findIndex((record) => record.handle === saved.handle);
      if (index < 0) return;
      renderUpTo(Math.max(Number(saved.rendered) || 0, index + 1));
      const card = cards.get(saved.handle);
      if (!card) return;
      jump(() => card.scrollIntoView({ block: 'center' }));
      card.focus({ preventScroll: true });
    }

    // -------------------------------------------------------------------------
    // Search + suggestions (combobox)
    // -------------------------------------------------------------------------
    function onSearchInput() {
      el.searchClear.hidden = el.searchInput.value.length === 0;
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        const raw = el.searchInput.value.trim().slice(0, 100);
        const value = raw.length >= SEARCH_MIN_CHARS ? raw : '';
        if (value !== state.query) {
          state.query = value;
          update({ keepScroll: true });
        }
        renderSuggestions();
      }, 150);
    }

    function renderSuggestions() {
      const raw = el.searchInput.value.trim();
      const tokens = raw.length >= SEARCH_MIN_CHARS ? fold(raw).split(/\s+/).filter(Boolean) : [];
      if (!tokens.length || document.activeElement !== el.searchInput) return hideSuggestions();

      const items = [];

      if (state.status === 'ready') {
        const people = [];
        for (const record of state.records) {
          if (!record.searchKey) record.searchKey = fold(record.name);
          if (tokens.every((token) => record.searchKey.includes(token))) people.push(record);
          if (people.length === 5) break;
        }
        if (people.length) {
          items.push({ heading: 'Instructors' });
          for (const record of people) {
            items.push({ type: 'profile', label: record.name, meta: locationText(record), url: record.url });
          }
        }
      }

      const filterMatches = [];
      for (const group of GROUPS) {
        for (let i = 0; i < group.options.length && filterMatches.length < 6; i++) {
          const value = group.options[i];
          if (state.filters[group.key].has(value)) continue;
          const count = state.status === 'ready' ? state.counts[group.key].get(value) || 0 : null;
          if (count === 0) continue;
          if (tokens.every((token) => group.folded[i].includes(token))) {
            filterMatches.push({ type: 'filter', group: group.key, value, label: value, meta: group.label, count });
          }
        }
      }
      if (filterMatches.length) items.push({ heading: 'Filters' }, ...filterMatches);

      if (!items.length) return hideSuggestions();

      const nodes = [];
      let optionIndex = 0;
      for (const item of items) {
        const li = document.createElement('li');
        if (item.heading) {
          li.className = 'ipd-suggest__heading';
          li.setAttribute('role', 'presentation');
          li.textContent = item.heading;
          nodes.push(li);
          continue;
        }
        li.className = 'ipd-suggest__option';
        li.id = `${el.suggestions.id}-${optionIndex++}`;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.dataset.type = item.type;
        if (item.url) li.dataset.url = item.url;
        if (item.group) {
          li.dataset.group = item.group;
          li.dataset.value = item.value;
        }
        const label = document.createElement('span');
        label.className = 'ipd-suggest__label';
        label.textContent = item.label;
        li.append(label);
        if (item.meta) {
          const meta = document.createElement('span');
          meta.className = 'ipd-suggest__meta';
          meta.textContent =
            item.count === null || item.count === undefined ? item.meta : `${item.meta} · ${numberFormat.format(item.count)}`;
          li.append(meta);
        }
        nodes.push(li);
      }

      el.suggestions.replaceChildren(...nodes);
      el.suggestions.hidden = false;
      el.searchInput.setAttribute('aria-expanded', 'true');
      setActiveSuggestion(-1);
    }

    function hideSuggestions() {
      el.suggestions.hidden = true;
      el.suggestions.replaceChildren();
      el.searchInput.setAttribute('aria-expanded', 'false');
      el.searchInput.removeAttribute('aria-activedescendant');
      state.suggestionIndex = -1;
    }

    function suggestionOptions() {
      return $$('[role="option"]', el.suggestions);
    }

    function setActiveSuggestion(index) {
      const options = suggestionOptions();
      state.suggestionIndex = index;
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
      if (index >= 0 && options[index]) {
        el.searchInput.setAttribute('aria-activedescendant', options[index].id);
        options[index].scrollIntoView({ block: 'nearest' });
      } else {
        el.searchInput.removeAttribute('aria-activedescendant');
      }
    }

    function chooseSuggestion(option) {
      if (!option) return;
      if (option.dataset.type === 'profile' && option.dataset.url) {
        writeStateToURL();
        window.location.href = option.dataset.url;
        return;
      }
      if (option.dataset.type === 'filter') {
        setFilter(option.dataset.group, option.dataset.value, true);
        state.query = '';
        el.searchInput.value = '';
        hideSuggestions();
        update();
        el.searchInput.focus();
      }
    }

    function onSearchKeydown(event) {
      const options = suggestionOptions();
      const open = !el.suggestions.hidden && options.length > 0;
      if (event.key === 'ArrowDown' && open) {
        event.preventDefault();
        setActiveSuggestion((state.suggestionIndex + 1) % options.length);
      } else if (event.key === 'ArrowUp' && open) {
        event.preventDefault();
        setActiveSuggestion(state.suggestionIndex <= 0 ? options.length - 1 : state.suggestionIndex - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (open && state.suggestionIndex >= 0) {
          chooseSuggestion(options[state.suggestionIndex]);
        } else {
          clearTimeout(searchTimer);
          const raw = el.searchInput.value.trim().slice(0, 100);
          state.query = raw.length >= SEARCH_MIN_CHARS ? raw : '';
          hideSuggestions();
          update();
        }
      } else if (event.key === 'Escape') {
        if (open) {
          event.preventDefault();
          hideSuggestions();
        } else if (el.searchInput.value) {
          event.preventDefault();
          clearSearch();
        }
      }
    }

    function clearSearch() {
      clearTimeout(searchTimer);
      el.searchInput.value = '';
      state.query = '';
      hideSuggestions();
      update({ keepScroll: true });
      el.searchInput.focus();
    }

    // -------------------------------------------------------------------------
    // Desktop dropdowns
    // -------------------------------------------------------------------------
    function toggleDropdown(dropdown) {
      if (openDropdown === dropdown) return closeDropdown({ focusToggle: false });
      closeDropdown({ focusToggle: false });
      openDropdown = dropdown;
      const toggle = $('[data-ipd-dropdown-toggle]', dropdown);
      const panel = $('[data-ipd-dropdown-panel]', dropdown);
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      fitPanel(dropdown, panel);
      $('.ipd-panel__list', panel).scrollTop = 0;
      // Only jump into the panel search on mouse/trackpad devices: on touch screens
      // focusing an input opens the on-screen keyboard.
      const search = $('[data-ipd-option-search]', panel);
      if (search && window.matchMedia('(pointer: fine)').matches) search.focus({ preventScroll: true });
    }

    // Keeps the whole panel — including Clear / Done — on screen: scrolls the
    // page up a little if needed, then caps the panel height to the space left.
    function fitPanel(dropdown, panel) {
      const margin = 12;
      const wanted = Math.min(440, Math.round(window.innerHeight * 0.62));
      let rect = dropdown.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - margin;
      if (below < wanted) {
        const room = rect.top - stickyOffset() - margin;
        const shift = Math.min(room, wanted - below);
        if (shift > 0) {
          jump(() => window.scrollBy(0, shift));
          rect = dropdown.getBoundingClientRect();
        }
      }
      const available = window.innerHeight - rect.bottom - margin;
      panel.style.maxHeight = `${Math.max(220, Math.min(wanted, available))}px`;
    }

    function closeDropdown({ focusToggle }) {
      if (!openDropdown) return;
      const dropdown = openDropdown;
      openDropdown = null;
      const toggle = $('[data-ipd-dropdown-toggle]', dropdown);
      $('[data-ipd-dropdown-panel]', dropdown).hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      resetOptionSearch(dropdown);
      if (focusToggle) toggle.focus();
    }

    function filterOptionList(searchInput) {
      const scope = searchInput.closest('[data-ipd-option-scope]');
      const tokens = fold(searchInput.value.trim()).split(/\s+/).filter(Boolean);
      let visible = 0;
      for (const option of $$('.ipd-option', scope)) {
        const show = tokens.every((token) => option.dataset.value.includes(token));
        option.hidden = !show;
        if (show) visible++;
      }
      const none = $('[data-ipd-option-none]', scope);
      if (none) none.hidden = visible > 0;
      if (scope.matches('[data-ipd-dropdown-panel]')) resetRoving(scope);
    }

    function resetOptionSearch(scope) {
      const search = $('[data-ipd-option-search]', scope);
      if (search && search.value) {
        search.value = '';
        filterOptionList(search);
      }
    }

    function panelOptions(panel) {
      return $$('.ipd-option:not([hidden]) .ipd-option__input:not(:disabled)', panel);
    }

    // Exactly one option in a desktop panel is reachable with Tab.
    function resetRoving(panel) {
      const inputs = $$('.ipd-option__input', panel);
      inputs.forEach((input) => {
        input.tabIndex = -1;
      });
      const first = panelOptions(panel)[0];
      if (first) first.tabIndex = 0;
    }

    function moveInPanel(event) {
      const panel = $('[data-ipd-dropdown-panel]', openDropdown);
      const options = panelOptions(panel);
      const search = $('[data-ipd-option-search]', panel);
      const items = search ? [search, ...options] : options;
      if (!items.length) return;
      const current = items.indexOf(document.activeElement);
      let next;
      if (event.key === 'Home') next = search ? 1 : 0;
      else if (event.key === 'End') next = items.length - 1;
      else if (event.key === 'ArrowDown') next = Math.min(current + 1, items.length - 1);
      else next = Math.max(current - 1, 0);
      const target = items[Math.min(next, items.length - 1)];
      if (!target) return;
      event.preventDefault();
      if (target !== search) {
        options.forEach((input) => {
          input.tabIndex = input === target ? 0 : -1;
        });
      }
      target.focus();
      target.closest('.ipd-option')?.scrollIntoView({ block: 'nearest' });
    }

    // -------------------------------------------------------------------------
    // Mobile filter sheet (<dialog>)
    // -------------------------------------------------------------------------
    function openSheet() {
      if (!el.dialog || el.dialog.open) return;
      if (!state.sheetRendered) {
        state.sheetRendered = true;
        for (const container of $$('[data-ipd-options]', el.dialog)) renderOptions(container, false);
        syncControls();
      }
      hideSuggestions();
      el.dialog.showModal();
      // Scroll lock for browsers without :has() (Firefox ESR 115).
      document.documentElement.classList.add('ipd-scroll-lock');
    }

    function onSheetClose() {
      document.documentElement.classList.remove('ipd-scroll-lock');
      for (const group of el.sheetGroups) resetOptionSearch(group);
      if (state.gridStale && state.status === 'ready') {
        renderGrid();
        if (state.restorePending) restoreReturnPosition();
        else scrollToResults();
      }
    }

    // -------------------------------------------------------------------------
    // Back to top
    // -------------------------------------------------------------------------
    function onScroll() {
      if (toTopFrame) return;
      toTopFrame = requestAnimationFrame(() => {
        toTopFrame = 0;
        el.toTop.hidden = window.scrollY < window.innerHeight * 2;
      });
    }

    // -------------------------------------------------------------------------
    // Events (delegated — one listener per area)
    // -------------------------------------------------------------------------
    function bindEvents() {
      el.searchInput.addEventListener('input', onSearchInput);
      el.searchInput.addEventListener('keydown', onSearchKeydown);
      el.searchInput.addEventListener('focus', renderSuggestions);
      el.searchInput.addEventListener('blur', () => setTimeout(hideSuggestions, 150));
      el.searchClear.addEventListener('click', clearSearch);

      // mousedown's default would move focus out of the input and close the list
      // before the click lands. Choosing happens on click, so a finger swipe that
      // scrolls the list never opens a profile by accident.
      el.suggestions.addEventListener('mousedown', (event) => event.preventDefault());
      el.suggestions.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) chooseSuggestion(option);
      });

      root.addEventListener('change', (event) => {
        const input = event.target;
        if (!input.matches('input[data-group]')) return;
        setFilter(input.dataset.group, input.value, input.checked);
        update({ keepScroll: true });
      });

      root.addEventListener('input', (event) => {
        if (event.target.matches('[data-ipd-option-search]')) filterOptionList(event.target);
      });

      root.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-ipd-dropdown-toggle]');
        if (toggle) return toggleDropdown(toggle.closest('[data-ipd-dropdown]'));

        const pill = event.target.closest('.ipd-pill');
        if (pill) {
          setFilter(pill.dataset.group, pill.dataset.value, false);
          return update({ keepScroll: true });
        }

        if (event.target.closest('[data-ipd-clear-all]')) {
          clearFilters({ includeSearch: true });
          return update({ keepScroll: true });
        }

        if (event.target.closest('[data-ipd-empty-action]')) {
          clearFilters({ includeSearch: !hasActiveFilters() });
          return update({ keepScroll: true });
        }

        const groupClear = event.target.closest('[data-ipd-group-clear]');
        if (groupClear) {
          state.filters[groupClear.dataset.ipdGroupClear]?.clear();
          update({ keepScroll: true });
          // The Clear button hides itself; keep focus inside the open panel.
          const done = $('[data-ipd-dropdown-done]', groupClear.closest('[data-ipd-dropdown]') || root);
          if (openDropdown && done) done.focus();
          return;
        }

        if (event.target.closest('[data-ipd-dropdown-done]')) return closeDropdown({ focusToggle: true });

        if (event.target.closest('[data-ipd-sheet-clear]')) {
          clearFilters({ includeSearch: false });
          return update({ keepScroll: true });
        }

        if (event.target.closest('[data-ipd-dialog-open]')) return openSheet();
        if (event.target.closest('[data-ipd-dialog-close]')) return el.dialog.close();
        if (event.target.closest('[data-ipd-retry]')) return load();
        if (event.target.closest('[data-ipd-to-top]')) {
          const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          return window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
        }

        const card = event.target.closest('.ipd-card[data-handle]');
        if (card) rememberReturnPoint(card.dataset.handle);
      });

      root.addEventListener('focusin', (event) => {
        // Remember the last focused option as the panel's tab stop.
        const input = event.target.closest('[data-ipd-dropdown-panel] .ipd-option__input');
        if (!input) return;
        for (const other of $$('.ipd-option__input', input.closest('[data-ipd-dropdown-panel]'))) {
          other.tabIndex = other === input ? 0 : -1;
        }
      });

      root.addEventListener('focusout', (event) => {
        if (openDropdown && event.relatedTarget && !openDropdown.contains(event.relatedTarget)) {
          closeDropdown({ focusToggle: false });
        }
      });

      document.addEventListener(
        'pointerdown',
        (event) => {
          if (openDropdown && !openDropdown.contains(event.target)) closeDropdown({ focusToggle: false });
        },
        { signal }
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (!openDropdown) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            closeDropdown({ focusToggle: true });
          } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) && openDropdown.contains(event.target)) {
            // Home/End stay with the text cursor while typing in the panel search.
            if ((event.key === 'Home' || event.key === 'End') && event.target.matches('[data-ipd-option-search]')) return;
            moveInPanel(event);
          }
        },
        { signal }
      );

      if (el.dialog) {
        el.dialog.addEventListener('close', onSheetClose);
        // A tap on the backdrop (outside the sheet content) closes it.
        el.dialog.addEventListener('click', (event) => {
          if (event.target === el.dialog) el.dialog.close();
        });
        // Switching to the desktop layout while the sheet is open.
        const desktop = window.matchMedia('(min-width: 750px)');
        const onLayoutChange = (event) => {
          if (event.matches && el.dialog.open) el.dialog.close();
        };
        desktop.addEventListener('change', onLayoutChange);
        signal.addEventListener('abort', () => desktop.removeEventListener('change', onLayoutChange));
      }

      window.addEventListener('scroll', onScroll, { passive: true, signal });
      // Leaving the page: make sure the URL already holds the latest filters.
      window.addEventListener('pagehide', writeStateToURL, { signal });
    }
  }

  // ---------------------------------------------------------------------------
  // Pure helpers
  // ---------------------------------------------------------------------------
  function emptyCounts() {
    return Object.fromEntries(GROUPS.map((group) => [group.key, new Map()]));
  }

  function toRecord(node) {
    if (!node?.handle) return null;
    const list = (field) => unique(parseList(field?.value).map(cleanValue).filter(Boolean));
    const image = node.image?.reference?.image;
    const name = cleanText(node.name?.value) || nameFromHandle(node.handle);
    const priority = Number.parseInt(node.priority?.value, 10);

    return {
      handle: node.handle,
      url: profilePath(node.onlineStoreUrl, node.handle),
      name,
      countries: list(node.country),
      region: list(node.region)[0] || '',
      city: list(node.city)[0] || '',
      specialties: list(node.specialties),
      levels: list(node.levels),
      languages: list(node.languages),
      priority: Number.isFinite(priority) && priority > 0 ? priority : null,
      image: image?.url
        ? {
            url: image.url,
            width: image.width || 0,
            height: image.height || 0,
            alt: cleanText(image.altText),
            thumbhash: image.thumbhash || ''
          }
        : null
    };
  }

  function defaultProfilePath(handle) {
    return `/pages/instructor/${encodeURIComponent(handle)}`;
  }

  function profilePath(onlineStoreUrl, handle) {
    try {
      if (onlineStoreUrl) {
        const url = new URL(onlineStoreUrl);
        if (url.protocol === 'https:' || url.protocol === 'http:') return url.pathname;
      }
    } catch {
      // fall through to the default profile path
    }
    return defaultProfilePath(handle);
  }

  // Only same-site paths ("/pages/…"), never "//host" or "javascript:".
  function safeProfilePath(path, handle) {
    return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') ? path : defaultProfilePath(handle);
  }

  function sortLevels(levels) {
    const rank = (level) => {
      const i = LEVEL_ORDER.indexOf(level);
      return i < 0 ? LEVEL_ORDER.length : i;
    };
    return [...levels].sort((a, b) => rank(a) - rank(b));
  }

  function cdnUrl(url, width) {
    // Resized original (keeps its `v` version parameter). The card crops it with
    // object-fit, focused on the upper part so portrait photos keep the face.
    // The CDN picks WebP/AVIF/JPEG from the browser's Accept header by itself.
    try {
      const u = new URL(url);
      if (u.hostname !== 'cdn.shopify.com' && !u.pathname.includes('/cdn/shop/')) return url;
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch {
      return url;
    }
  }

  function initialsFor(name) {
    const span = document.createElement('span');
    span.className = 'ipd-card__initials';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('');
    return span;
  }

  // Country first, then region and city — the same order as the filters.
  function locationText(record) {
    const [country, ...otherCountries] = record.countries;
    const parts = unique([country, record.region, record.city].filter(Boolean));
    const text = parts.join(', ');
    if (!otherCountries.length) return text;
    return `${text} +${otherCountries.length}`;
  }

  function closeIcon() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M5 5l10 10M15 5L5 15');
    svg.append(path);
    return svg;
  }

  function createStorage() {
    return {
      get(key) {
        try {
          return sessionStorage.getItem(key);
        } catch {
          return null;
        }
      },
      set(key, value) {
        try {
          sessionStorage.setItem(key, value);
        } catch {
          // quota exceeded or storage disabled: the page still works without it
        }
      },
      remove(key) {
        try {
          sessionStorage.removeItem(key);
        } catch {
          // ignore
        }
      }
    };
  }

  function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
      a = (a + 0x6d2b79f5) >>> 0;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function parseList(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [value];
    }
  }

  // Exact matching against the official lists: only surrounding whitespace and
  // dash variants (– — ―) are tolerated, as before.
  function cleanValue(value) {
    return String(value ?? '')
      .replace(/[–—―]/g, '-')
      .trim();
  }

  function cleanText(value) {
    return String(value ?? '').replace(/\s+/g, ' ').trim();
  }

  function unique(values) {
    return Array.from(new Set(values));
  }

  // Accent-insensitive matching: "jose" finds "José", "lukasz" finds "Łukasz".
  function fold(value) {
    return String(value)
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .replace(/[łøđðıæœßþ]/g, (ch) => FOLD_EXTRA[ch]);
  }

  function nameFromHandle(handle) {
    return handle
      .replace(/-\d+$/, '')
      .split('-')
      .filter(Boolean)
      .map((part) => part[0].toUpperCase() + part.slice(1))
      .join(' ');
  }

  // ThumbHash (https://evanw.github.io/thumbhash/, MIT) — decodes the tiny
  // placeholder Shopify returns for each image into a blurred preview.
  function thumbhashDataURL(base64) {
    if (!base64) return '';
    try {
      const hash = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      const { w, h, rgba } = thumbHashToRGBA(hash);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(rgba.buffer), w, h), 0, 0);
      return canvas.toDataURL();
    } catch {
      return '';
    }
  }

  function thumbHashToRGBA(hash) {
    const { PI, min, max, cos, round } = Math;
    const header24 = hash[0] | (hash[1] << 8) | (hash[2] << 16);
    const header16 = hash[3] | (hash[4] << 8);
    const lDc = (header24 & 63) / 63;
    const pDc = ((header24 >> 6) & 63) / 31.5 - 1;
    const qDc = ((header24 >> 12) & 63) / 31.5 - 1;
    const lScale = ((header24 >> 18) & 31) / 31;
    const hasAlpha = header24 >> 23;
    const pScale = ((header16 >> 3) & 63) / 63;
    const qScale = ((header16 >> 9) & 63) / 63;
    const isLandscape = header16 >> 15;
    const lx = max(3, isLandscape ? (hasAlpha ? 5 : 7) : header16 & 7);
    const ly = max(3, isLandscape ? header16 & 7 : hasAlpha ? 5 : 7);
    const aDc = hasAlpha ? (hash[5] & 15) / 15 : 1;
    const aScale = (hash[5] >> 4) / 15;

    const acStart = hasAlpha ? 6 : 5;
    let acIndex = 0;
    const decodeChannel = (nx, ny, scale) => {
      const ac = [];
      for (let cy = 0; cy < ny; cy++) {
        for (let cx = cy ? 0 : 1; cx * ny < nx * (ny - cy); cx++) {
          ac.push((((hash[acStart + (acIndex >> 1)] >> ((acIndex++ & 1) << 2)) & 15) / 7.5 - 1) * scale);
        }
      }
      return ac;
    };
    const lAc = decodeChannel(lx, ly, lScale);
    const pAc = decodeChannel(3, 3, pScale * 1.25);
    const qAc = decodeChannel(3, 3, qScale * 1.25);
    const aAc = hasAlpha && decodeChannel(5, 5, aScale);

    const ratio = thumbHashAspectRatio(hash);
    const w = round(ratio > 1 ? 32 : 32 * ratio);
    const h = round(ratio > 1 ? 32 / ratio : 32);
    const rgba = new Uint8Array(w * h * 4);
    const fx = [];
    const fy = [];
    for (let y = 0, i = 0; y < h; y++) {
      for (let x = 0; x < w; x++, i += 4) {
        let l = lDc;
        let p = pDc;
        let q = qDc;
        let a = aDc;
        for (let cx = 0, n = max(lx, hasAlpha ? 5 : 3); cx < n; cx++) fx[cx] = cos((PI / w) * (x + 0.5) * cx);
        for (let cy = 0, n = max(ly, hasAlpha ? 5 : 3); cy < n; cy++) fy[cy] = cos((PI / h) * (y + 0.5) * cy);
        for (let cy = 0, j = 0; cy < ly; cy++) {
          for (let cx = cy ? 0 : 1, fy2 = fy[cy] * 2; cx * ly < lx * (ly - cy); cx++, j++) l += lAc[j] * fx[cx] * fy2;
        }
        for (let cy = 0, j = 0; cy < 3; cy++) {
          for (let cx = cy ? 0 : 1, fy2 = fy[cy] * 2; cx < 3 - cy; cx++, j++) {
            const f = fx[cx] * fy2;
            p += pAc[j] * f;
            q += qAc[j] * f;
          }
        }
        if (hasAlpha) {
          for (let cy = 0, j = 0; cy < 5; cy++) {
            for (let cx = cy ? 0 : 1, fy2 = fy[cy] * 2; cx < 5 - cy; cx++, j++) a += aAc[j] * fx[cx] * fy2;
          }
        }
        const b = l - (2 / 3) * p;
        const r = (3 * l - b + q) / 2;
        const g = r - q;
        rgba[i] = max(0, 255 * min(1, r));
        rgba[i + 1] = max(0, 255 * min(1, g));
        rgba[i + 2] = max(0, 255 * min(1, b));
        rgba[i + 3] = max(0, 255 * min(1, a));
      }
    }
    return { w, h, rgba };
  }

  function thumbHashAspectRatio(hash) {
    const header = hash[3];
    const hasAlpha = hash[2] & 0x80;
    const isLandscape = hash[4] & 0x80;
    const lx = isLandscape ? (hasAlpha ? 5 : 7) : header & 7;
    const ly = isLandscape ? header & 7 : hasAlpha ? 5 : 7;
    return lx / ly;
  }
})();
